import {useEffect, useMemo, useRef, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {History, MoveRight, Plus, ShieldAlert} from "lucide-react";
import {Link} from "react-router-dom";
import {
    COUNTRY_SCOPE_STORAGE_KEY,
    getAdminCountryScope,
    resolveAdminCountryScope,
} from "../../shared/api/countryScope";
import {getAuthToken} from "../../shared/api/client";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {AdminTable} from "../../shared/components/AdminTable";
import {DataState} from "../../shared/components/DataState";
import {MutationResultBanner} from "../../shared/components/MutationResultBanner";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {formatExactMoney} from "../../shared/finance/decimal";
import {mapFinanceError} from "../../shared/finance/errors";
import {getActorCountryScopeBlock} from "../../shared/finance/actorCountryScope";
import {formatDateTime} from "../../shared/utils/format";
import i18n, {useTranslation} from "../../shared/i18n";
import {
    createRefundRequest,
    listRefundRequests,
    refundKeys,
} from "./api";
import {RefundRequestDialog} from "./components/RefundRequestDialog";
import {
    getRefundStatusPresentation,
    hasPostedRefund,
} from "./statusMachine";
import "./refunds.css";
import {PageSizeSelect} from "../../shared/components/PageSizeSelect";

const STATUS_OPTIONS = [
    "requested",
    "approved",
    "rejected",
    "posted",
    "failed",
    "cancelled",
];

let fallbackIdempotencySequence = 0;

export const generateRefundIdempotencyKey = () => {
    if (typeof window.crypto?.randomUUID === "function") {
        return window.crypto.randomUUID();
    }
    if (process.env.NODE_ENV === "test") {
        fallbackIdempotencySequence += 1;
        return `refund-request-test-${String(fallbackIdempotencySequence).padStart(6, "0")}`;
    }
    throw new Error(i18n.t("money.refunds.idempotencyUnsupported"));
};

const resultPresentation = (request, t) => {
    const statusLabel = t(`money.refunds.statuses.${getRefundStatusPresentation(request.status).known ? request.status : "unknown"}`);
    if (request.status === "requested") {
        return {
            banner: {kind: "success", message: t("money.refunds.createdPending")},
            link: {label: t("money.refunds.openRequest"), to: `/finance/refunds/${request.id}`},
        };
    }
    if (request.status === "posted" && hasPostedRefund(request)) {
        return {
            banner: {
                kind: "success",
                message: t("money.refunds.posted", {id: request.posted_refund_id}),
            },
            link: {
                label: t("money.refunds.openHistory"),
                to: `/finance/refund-history?payment_bill_id=${encodeURIComponent(request.pay_bill_id)}`,
            },
        };
    }
    if (request.status === "failed") {
        return {
            banner: {
                kind: "error",
                message: t("money.refunds.postingFailed", {
                    code: request.failure_code || t("money.refunds.unknownFailure"),
                }),
            },
            link: {label: t("money.refunds.openRequest"), to: `/finance/refunds/${request.id}`},
        };
    }
    return {
        banner: {
            kind: "error",
            message: request.status === "posted"
                ? t("money.refunds.postedUnconfirmed")
                : t("money.refunds.unexpectedStatus", {status: statusLabel}),
        },
        link: {label: t("money.refunds.openRequest"), to: `/finance/refunds/${request.id}`},
    };
};

export const RefundsPage = () => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const capabilities = useCapabilities();
    const [countryScope, setCountryScope] = useState(() =>
        resolveAdminCountryScope({
            role: capabilities.user?.role,
            allowedCountryIds: capabilities.scope?.country_ids || [],
            storedScope:
                localStorage.getItem(COUNTRY_SCOPE_STORAGE_KEY) ?? getAdminCountryScope(),
        }),
    );
    const activeCountryScopeRef = useRef(countryScope);
    const [status, setStatus] = useState("");
    const [limit, setLimit] = useState(50);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [pendingRequest, setPendingRequest] = useState(null);
    const [mutationResult, setMutationResult] = useState(null);
    const [resultLink, setResultLink] = useState(null);
    const filters = useMemo(() => ({status, limit}), [limit, status]);
    const globalScope = countryScope === "0";
    const scopeBlock = getActorCountryScopeBlock({
        countryScope,
        actorCountryId: capabilities.country?.id,
    });
    const foreignScope = Boolean(scopeBlock) && !globalScope;

    useEffect(() => {
        const handleCountryChange = (event) => {
            const nextScope = event.detail?.countryId;
            const normalizedScope = nextScope == null ? null : String(nextScope);
            activeCountryScopeRef.current = normalizedScope;
            setCountryScope(normalizedScope);
            setStatus("");
            setLimit(50);
            setDialogOpen(false);
            setPendingRequest(null);
            setMutationResult(null);
            setResultLink(null);
        };
        window.addEventListener("sorpa-admin-country-scope-change", handleCountryChange);
        return () =>
            window.removeEventListener("sorpa-admin-country-scope-change", handleCountryChange);
    }, []);

    const requestsQuery = useQuery({
        queryKey: refundKeys.requestList(countryScope, filters),
        queryFn: () => listRefundRequests({
            countryScope,
            actorCountryId: capabilities.country?.id,
            status,
            limit,
        }),
        enabled: countryScope !== null && scopeBlock === null,
        retry: false,
    });

    const createMutation = useMutation({
        mutationFn: ({record, requestScope}) => createRefundRequest({
            countryScope: requestScope,
            actorCountryId: capabilities.country?.id,
            paymentBillId: record.paymentBillId,
            payload: record.payload,
            idempotencyKey: record.idempotencyKey,
        }),
        onSuccess: (request, variables) => {
            if (getAuthToken() !== variables.requestSession) return;
            void queryClient.invalidateQueries({
                queryKey: refundKeys.requestLists(variables.requestScope),
            });
            if (hasPostedRefund(request)) {
                void queryClient.invalidateQueries({
                    queryKey: refundKeys.historyRoot(variables.requestScope),
                });
            }
            if (activeCountryScopeRef.current !== variables.requestScope) return;
            const presentation = resultPresentation(request, t);
            setPendingRequest(null);
            setDialogOpen(false);
            setMutationResult(presentation.banner);
            setResultLink(presentation.link);
        },
        onError: (error, variables) => {
            if (
                getAuthToken() !== variables.requestSession ||
                activeCountryScopeRef.current !== variables.requestScope
            ) {
                return;
            }
            const mapped = mapFinanceError(error);
            if (mapped.uncertain) {
                setMutationResult({...mapped, kind: "uncertain"});
                return;
            }
            setPendingRequest(null);
            setMutationResult(mapped);
        },
    });

    const submitRecord = (record) => {
        setMutationResult(null);
        setResultLink(null);
        setPendingRequest(record);
        createMutation.mutate({
            record,
            requestScope: countryScope,
            requestSession: getAuthToken(),
        });
    };

    const closeDialog = () => {
        if (createMutation.isPending) return;
        setDialogOpen(false);
        setPendingRequest(null);
        setMutationResult(null);
    };

    const rows = requestsQuery.data || [];
    const columns = [
        {
            key: "bill",
            label: t("money.refunds.payment"),
            render: (item) => (
                <div>
                    <span className="admin-strong">{item.pay_bill_id}</span>
                    <div className="admin-muted">{t("money.refunds.requestId", {id: item.id})}</div>
                </div>
            ),
        },
        {
            key: "status",
            label: t("money.refunds.statusColumn"),
            render: (item) => {
                const presentation = getRefundStatusPresentation(item.status);
                return <StatusBadge tone={presentation.tone}>{t(`money.refunds.statuses.${presentation.known ? item.status : "unknown"}`)}</StatusBadge>;
            },
        },
        {
            key: "amount",
            label: t("money.refunds.amount"),
            render: (item) => item.amount === null
                ? t("money.refunds.full")
                : formatExactMoney(item.amount),
        },
        {key: "reason", label: t("money.refunds.reason"), render: (item) => item.reason || "—"},
        {
            key: "actors",
            label: t("money.refunds.actors"),
            render: (item) => (
                <div>
                    <span>{t("money.refunds.requestedBy", {id: item.requester_id})}</span>
                    <div className="admin-muted">
                        {item.approver_id ? t("money.refunds.decidedBy", {id: item.approver_id}) : t("money.refunds.noDecision")}
                    </div>
                </div>
            ),
        },
        {
            key: "timestamps",
            label: t("money.refunds.time"),
            render: (item) => (
                <div>
                    <span>{formatDateTime(item.requested_at)}</span>
                    <div className="admin-muted">
                        {item.posted_at ? t("money.refunds.postedAt", {time: formatDateTime(item.posted_at)}) : "—"}
                    </div>
                </div>
            ),
        },
        {
            key: "evidence",
            label: t("money.refunds.result"),
            render: (item) => (
                <div>
                    <span>{item.failure_code || "—"}</span>
                    <div className="admin-muted">{item.posted_refund_id || "—"}</div>
                </div>
            ),
        },
        {
            key: "action",
            label: "",
            render: (item) => (
                <Link
                    className="refund-row-link"
                    to={`/finance/refunds/${item.id}`}
                    aria-label={t("money.refunds.openAria", {id: item.id})}
                    title={t("money.refunds.open")}
                >
                    <MoveRight aria-hidden="true" size={17} />
                </Link>
            ),
        },
    ];

    return (
        <div className="admin-page refund-page">
            <header className="admin-page-header refund-page-header">
                <div>
                    <h1 className="admin-page-title">{t("money.refunds.title")}</h1>
                    <p className="admin-page-copy">
                        {t("money.refunds.copy")}
                    </p>
                </div>
                <div className="refund-header-actions">
                    <Link className="admin-button admin-button-secondary" to="/finance/refund-history">
                        <History aria-hidden="true" size={16} /> {t("money.refunds.history")}
                    </Link>
                    {capabilities.hasAction("refunds.request") && !foreignScope ? (
                        <button
                            className="admin-button admin-button-primary"
                            type="button"
                            disabled={globalScope}
                            onClick={() => {
                                setDialogOpen(true);
                                setPendingRequest(null);
                                setMutationResult(null);
                                setResultLink(null);
                            }}
                        >
                            <Plus aria-hidden="true" size={16} /> {t("money.refunds.create")}
                        </button>
                    ) : null}
                </div>
            </header>

            {scopeBlock ? (
                <p className="refund-scope-warning">
                    <ShieldAlert aria-hidden="true" size={16} />
                    {scopeBlock.title}
                </p>
            ) : null}

            <div className="admin-toolbar refund-filter-bar">
                <label className="admin-field">
                    <span>{t("money.refunds.status")}</span>
                    <select
                        aria-label={t("money.refunds.status")}
                        value={status}
                        onChange={(event) => setStatus(event.target.value)}
                    >
                        <option value="">{t("money.refunds.all")}</option>
                        {STATUS_OPTIONS.map((value) => (
                            <option key={value} value={value}>
                                {t(`money.refunds.statuses.${value}`)}
                            </option>
                        ))}
                    </select>
                </label>
                <PageSizeSelect value={limit} onChange={setLimit} />
            </div>

            {!dialogOpen ? <MutationResultBanner result={mutationResult} /> : null}
            {!dialogOpen && resultLink ? (
                <div className="refund-result-link">
                    <Link to={resultLink.to}>
                        {resultLink.label} <MoveRight aria-hidden="true" size={15} />
                    </Link>
                </div>
            ) : null}

            {scopeBlock ? (
                <DataState
                    title={scopeBlock.title}
                    copy={scopeBlock.copy}
                />
            ) : requestsQuery.isError ? (
                <DataState
                    title={t("money.refunds.loadError")}
                    copy={mapFinanceError(requestsQuery.error).message}
                />
            ) : (
                <AdminTable
                    columns={columns}
                    rows={rows}
                    loading={requestsQuery.isLoading}
                    emptyText={t("money.refunds.empty")}
                />
            )}

            <RefundRequestDialog
                open={dialogOpen}
                pending={createMutation.isPending}
                retryRecord={pendingRequest}
                error={mutationResult}
                onCancel={closeDialog}
                onConfirm={({paymentBillId, payload}) => submitRecord({
                    paymentBillId,
                    payload,
                    idempotencyKey: generateRefundIdempotencyKey(),
                })}
                onRetry={() => pendingRequest && submitRecord(pendingRequest)}
            />
        </div>
    );
};
